import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { PrimaryButton } from "@/components/fionova/Primitives";
import { submitInquiry } from "@/services/unsaidService";

const TYPES = [
  ["treatment_facility", "Treatment facility"], ["therapist_counselor", "Therapist or counselor"],
  ["recovery_coach", "Recovery coach"], ["wellness_professional", "Wellness professional"],
  ["organization", "Other organization"], ["other", "Something else"],
];
const FIELD = "w-full rounded-2xl bg-white/85 px-4 py-3 text-[15px] text-slate-900 ring-1 ring-slate-200 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500";

const Field = ({ label, children }) => (
  <label className="block"><span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>{children}</label>
);

// Professional partnership inquiry — stored server-side; never routes to consumer checkout.
export const PartnershipForm = () => {
  const [f, setF] = useState({ name: "", organization: "", role: "", email: "", interest_type: "", message: "", website: "" });
  const [state, setState] = useState("idle"); // idle | sending | done
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const ready = f.name.trim() && f.organization.trim() && f.role.trim() && f.email.trim() && f.interest_type;

  const send = async () => {
    if (!ready || state === "sending") return;
    setState("sending"); setError("");
    const res = await submitInquiry(f);
    if (res.ok) { setState("done"); return; }
    setState("idle");
    setError(res.data?.detail || "We couldn't send that just now. Please try again.");
  };

  if (state === "done") {
    return (
      <div className="flex items-start gap-3 rounded-3xl bg-teal-50/80 p-5 ring-1 ring-teal-200" data-testid="partnership-success">
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-teal-600" />
        <p className="text-[15px] leading-relaxed text-slate-700">Thank you — your inquiry has been received. Our team reviews every partnership inquiry and will follow up at the email you provided.</p>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="partnership-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name"><input data-testid="partnership-name" className={FIELD} value={f.name} onChange={set("name")} autoComplete="name" /></Field>
        <Field label="Organization"><input data-testid="partnership-organization" className={FIELD} value={f.organization} onChange={set("organization")} autoComplete="organization" /></Field>
        <Field label="Professional role"><input data-testid="partnership-role" className={FIELD} value={f.role} onChange={set("role")} placeholder="e.g. Clinical director" /></Field>
        <Field label="Professional email"><input data-testid="partnership-email" type="email" className={FIELD} value={f.email} onChange={set("email")} autoComplete="email" /></Field>
      </div>
      <Field label="Type of partnership interest">
        <select data-testid="partnership-type" className={FIELD} value={f.interest_type} onChange={set("interest_type")}>
          <option value="">Choose one…</option>
          {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </Field>
      <Field label="Message (optional)">
        <textarea data-testid="partnership-message" rows={4} maxLength={2000} className={FIELD} value={f.message} onChange={set("message")}
          placeholder="Tell us a little about your organization and how you imagine using FioNova." />
      </Field>
      <input tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" value={f.website} onChange={set("website")} name="website" />
      {error && <p className="text-sm text-rose-600" data-testid="partnership-error">{error}</p>}
      <PrimaryButton data-testid="partnership-submit" onClick={send} disabled={!ready || state === "sending"}>
        {state === "sending" ? "Sending…" : "Send partnership inquiry"}
      </PrimaryButton>
      <p className="text-center text-xs text-slate-500">Professional partnerships are arranged individually. Consumer subscription pricing doesn't apply.</p>
    </div>
  );
};
